import { useEffect, useState } from "react";

function pointInBox(lat, lon, b) {
  return (
    b &&
    lat >= b.latMin &&
    lat <= b.latMax &&
    lon >= b.lonMin &&
    lon <= b.lonMax
  );
}

function freshwaterOverlap(lat, lon, freshwaterData = []) {
  return freshwaterData.some((f) => f.bounds && pointInBox(lat, lon, f.bounds));
}

function findRegionData(dataArray, searchTerm) {
  if (!Array.isArray(dataArray)) return null;
  const searchLower = (searchTerm || "").toLowerCase();

  let match = dataArray.find(
    (item) => (item.region || "").toLowerCase() === searchLower
  );
  if (match) return match;

  match = dataArray.find((item) =>
    (item.region || "").toLowerCase().includes(searchLower)
  );
  if (match) return match;

  match = dataArray.find((item) =>
    searchLower.includes((item.region || "").toLowerCase())
  );
  if (match) return match;

  return null;
}

function detectJsonRegion(lat, lon) {
  const regions = [
    { name: "Mediterranean Sea", test: () => lat > 30 && lat < 46 && lon > -6 && lon < 42 },
    { name: "Caribbean Sea", test: () => lat > 10 && lat < 27 && lon > -88 && lon < -60 },
    { name: "North Sea", test: () => lat > 51 && lat < 62 && lon > -4 && lon < 9 },
    { name: "Pacific Ocean", test: () => (lon > 130 || lon < -110) && lat > -60 && lat < 60 },
    { name: "Indian Ocean", test: () => lat < 25 && lat > -50 && lon > 40 && lon < 120 },
    { name: "Arctic Ocean", test: () => lat >= 66 },
    { name: "Greece, Turkey & Bulgaria (Southeastern Europe)", test: () => lat > 35 && lat < 43 && lon > 19 && lon < 45 },
    { name: "Europe & Arctic", test: () => lat > 43 && lat < 72 && lon > -10 && lon < 65 },
    { name: "Florida, Gulf Coast & Southern US", test: () => lat > 24 && lat < 37 && lon > -106 && lon < -75 },
    { name: "California & Western USA", test: () => lat > 32 && lat < 49 && lon > -125 && lon < -110 },
    { name: "Central & Eastern USA (Tornado Alley)", test: () => lat > 35 && lat < 48 && lon > -106 && lon < -85 },
    { name: "Nile River Basin & Sub-Saharan Africa", test: () => lat > -10 && lat < 32 && lon > 10 && lon < 50 },
    { name: "Sub-Saharan Africa", test: () => lat < 15 && lat > -35 && lon > -20 && lon < 55 },
    { name: "Middle East & North Africa", test: () => lat > 15 && lat < 40 && lon > -15 && lon < 65 },
    { name: "South & Southeast Asia", test: () => lat > -10 && lat < 35 && lon > 60 && lon < 100 },
    { name: "Southeast Asia (Thailand, Vietnam, Cambodia, Malaysia)", test: () => lat > 0 && lat < 25 && lon > 95 && lon < 110 },
    { name: "Northern Australia, Papua New Guinea & Southeast Asia", test: () => lat > -20 && lat < 0 && lon > 110 && lon < 155 },
    { name: "Australia", test: () => lat > -45 && lat < -10 && lon > 110 && lon < 155 },
    { name: "Indonesia & Surrounding Islands", test: () => lat > -10 && lat < 10 && lon > 95 && lon < 145 },
    { name: "Pacific Ring of Fire (Japan, Indonesia, Philippines)", test: () => (lat > 25 && lat < 45 && lon > 125 && lon < 150) || (lat > -10 && lat < 15 && lon > 115 && lon < 145) },
    { name: "East Asia & Pacific", test: () => lat > 20 && lat < 50 && lon > 100 && lon < 145 },
    { name: "South America (Amazon & Coasts)", test: () => lat > -15 && lat < 10 && lon > -80 && lon < -35 },
    { name: "South America (Chile, Peru, Ecuador)", test: () => lat > -45 && lat < 5 && lon > -82 && lon < -65 },
    { name: "Latin America & Caribbean", test: () => lat > -35 && lat < 33 && lon > -120 && lon < -30 },
    { name: "Caribbean & Gulf of Mexico", test: () => lat > 10 && lat < 31 && lon > -100 && lon < -60 },
    { name: "Iceland & North Atlantic", test: () => lat > 60 && lat < 70 && lon > -25 && lon < -10 },
    { name: "South Africa, California & Southern Oceans", test: () => (lat > -35 && lat < -28 && lon > 15 && lon < 35) || (lat > 32 && lat < 42 && lon > -125 && lon < -115) },
  ];

  for (const r of regions) {
    if (r.test()) return r.name;
  }
  return "GLOBAL";
}

async function reverseGeocode(lat, lon) {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10&addressdetails=1`
    );
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

function getHabitatZone(place) {
  if (!place) return "land";

  const name = `${place?.name || ""} ${place?.display_name || ""}`.toLowerCase();
  const addr = place?.address || {};

  console.log("🔍 Analyzing place:", { name: place?.name, display: place?.display_name });

  if (
    /(ocean|sea|gulf|strait|channel|sound)/i.test(place?.name || "") ||
    /(ocean|sea|gulf|bay|strait|channel|sound)/i.test(
      `${addr.water || ""} ${addr.sea || ""} ${addr.ocean || ""}`
    )
  ) {
    console.log("✅ Detected as MARINE (water body name)");
    return "marine";
  }

  if (addr.town || addr.city || addr.village || addr.municipality) {
    console.log("✅ Detected as LAND (populated area)");
    return "land";
  }

  if (/(ocean|sea|gulf|bay|strait|channel)/i.test(place?.display_name || "")) {
    console.log("✅ Detected as MARINE (display name)");
    return "marine";
  }

  if (
    /(coast|shore|beach|harbor|harbour|port)/i.test(name) ||
    /(coast|beach|harbor|harbour)/i.test(`${addr.coast || ""}`)
  ) {
    console.log("✅ Detected as SHORELINE");
    return "shoreline";
  }

  if (addr.island || addr.islet || /(island|isle)/i.test(name)) {
    console.log("✅ Detected as SHORELINE (island)");
    return "shoreline";
  }

  console.log("✅ Detected as LAND (default)");
  return "land";
}

function tokensFromPlace(place) {
  const addr = place?.address || {};
  return [
    addr.country,
    addr.state,
    addr.region,
    addr.county,
    addr.island,
    addr.city,
    addr.town,
    addr.village,
    place?.name,
  ]
    .filter(Boolean)
    .map((s) => s.toString());
}

function matchByTokens(dataArray, tokens) {
  if (!Array.isArray(dataArray)) return null;
  const lowerTokens = tokens.map((t) => t.toLowerCase());
  return (
    dataArray.find((row) =>
      lowerTokens.some((t) => (row.region || "").toLowerCase().includes(t))
    ) || null
  );
}

function extractMarineKey(place) {
  if (!place) return null;
  const name = `${place?.name || ""} ${place?.display_name || ""}`;
  const m = name.match(/([A-Z][a-zA-Z]+\s(?:Ocean|Sea|Gulf|Bay|Strait|Channel))/);
  if (m && m[1]) return m[1];
  return null;
}

function findRegionDataSmart(dataArray, searchTerm, lat) {
  const base = findRegionData(dataArray, searchTerm);
  if (base) return base;

  if (lat >= 66) {
    const arcticAlt = dataArray.find((r) => /arctic/i.test(r.region || ""));
    if (arcticAlt) return arcticAlt;
  }

  if (/arctic\s*ocean/i.test(searchTerm)) {
    const euArc = dataArray.find((r) => /europe\s*&\s*arctic/i.test(r.region || ""));
    if (euArc) return euArc;
  }

  return null;
}

function nearCoast(lat, lon) {
  const coasts = [
    { latMin: 30, latMax: 46, lonMin: -6, lonMax: 42, coastBuffer: 1.5 },
    { latMin: 10, latMax: 27, lonMin: -88, lonMax: -60, coastBuffer: 1.5 },
    { latMin: 35, latMax: 60, lonMin: -10, lonMax: 2, coastBuffer: 1.5 },
    { latMin: 20, latMax: 45, lonMin: 120, lonMax: 145, coastBuffer: 1.5 },
    { latMin: -45, latMax: -8, lonMin: 110, lonMax: 155, coastBuffer: 2 },
    { latMin: -55, latMax: 60, lonMin: -130, lonMax: -70, coastBuffer: 2 },
    { latMin: -55, latMax: 60, lonMin: -80, lonMax: -40, coastBuffer: 2 },
  ];

  const inlandBoxes = [
    { latMin: -35, latMax: -18, lonMin: 128, lonMax: 140 },
    { latMin: 36, latMax: 50, lonMin: -8, lonMax: 15 },
    { latMin: 35, latMax: 55, lonMin: 45, lonMax: 100 },
    { latMin: -10, latMax: 10, lonMin: 10, lonMax: 35 },
    { latMin: 20, latMax: 50, lonMin: -105, lonMax: -85 },
    { latMin: -20, latMax: 0, lonMin: -70, lonMax: -45 },
  ];

  if (
    inlandBoxes.some(
      (b) => lat >= b.latMin && lat <= b.latMax && lon >= b.lonMin && lon <= b.lonMax
    )
  ) {
    return false;
  }

  return coasts.some(
    (b) =>
      lat >= b.latMin - (b.coastBuffer || 0) &&
      lat <= b.latMax + (b.coastBuffer || 0) &&
      lon >= b.lonMin - (b.coastBuffer || 0) &&
      lon <= b.lonMax + (b.coastBuffer || 0)
  );
}

function detectEnvironment(lat, lon, place, freshwaterData = []) {
  if (freshwaterOverlap(lat, lon, freshwaterData)) return "freshwater";

  if (lat >= 66 || lat <= -60) return "marine";

  const marineKey = getMarineRegionName(lat, lon, place);
  if (marineKey) return "marine";

  const inlandBoxes = [
    { latMin: -35, latMax: -18, lonMin: 125, lonMax: 140 },
    { latMin: 35, latMax: 55, lonMin: 45, lonMax: 100 },
    { latMin: 15, latMax: 30, lonMin: -15, lonMax: 35 },
    { latMin: 20, latMax: 50, lonMin: -105, lonMax: -85 },
    { latMin: -25, latMax: 0, lonMin: -70, lonMax: -45 },
  ];
  if (
    inlandBoxes.some(
      (b) =>
        lat >= b.latMin &&
        lat <= b.latMax &&
        lon >= b.lonMin &&
        lon <= b.lonMax
    )
  ) {
    return "freshwater";
  }

  const inOcean =
    ((lat > -50 && lat < 65) && (lon > -80 && lon < -10)) ||
    ((lat > -60 && lat < 60) && (lon > 130 || lon < -110)) ||
    ((lat > -45 && lat < 25) && (lon > 40 && lon < 115)) ||
    ((lat > 10 && lat < 27) && (lon > -88 && lon < -60)) ||
    ((lat > 30 && lat < 46) && (lon > -6 && lon < 42));

  if (inOcean) return "marine";

  if (nearCoast(lat, lon) && !freshwaterOverlap(lat, lon, freshwaterData)) {
    return "marine";
  }

  return "freshwater";
}

function getMarineRegionName(lat, lon, place) {
  const marineName = extractMarineKey(place);
  if (marineName) return marineName;

  const addr = place?.address || {};
  const name = `${place?.name || ""} ${place?.display_name || ""}`.toLowerCase();

  if (addr.city || addr.town || addr.village || addr.municipality) return null;
  if (
    /(mountain|region|province|county|municipality|village|city|state|inland)/i.test(name)
  ) {
    return null;
  }

  if (
    /(black|barents|greenland|arctic|north\s*sea|mediterranean|aegean|ionian|adriatic|levantine|caribbean|indian|pacific|atlantic)/i.test(
      name
    )
  ) {
    if (/black/i.test(name)) return "Black Sea";
    if (/barents|greenland|arctic/i.test(name)) return "Arctic Ocean";
    if (/north\s*sea/i.test(name)) return "North Sea";
    if (/mediterranean|aegean|ionian|adriatic|levantine/i.test(name)) {
      return "Mediterranean Sea";
    }
    if (/caribbean/i.test(name)) return "Caribbean Sea";
    if (/indian/i.test(name)) return "Indian Ocean";
    if (/pacific/i.test(name)) return "Pacific Ocean";
    if (/atlantic/i.test(name)) return "North Atlantic Ocean";
  }

  if (lat > 40 && lat < 47 && lon > 27 && lon < 42) return "Black Sea";
  if (lat > 51 && lat < 62 && lon > -4 && lon < 9) return "North Sea";
  if (lat > 65 && lat < 85 && ((lon > -180 && lon < -30) || (lon > 10 && lon < 100))) {
    return "Arctic Ocean";
  }

  if (lat > 10 && lat < 27 && lon > -88 && lon < -60) return "Caribbean Sea";
  if (lat < 25 && lat > -50 && lon > 40 && lon < 120) return "Indian Ocean";
  if ((lon > 130 || lon < -110) && lat > -60 && lat < 60) return "Pacific Ocean";
  if (lon > -80 && lon < -10 && lat > -60 && lat < 65) return "North Atlantic Ocean";
  if (lat > 30 && lat < 46 && lon > -6 && lon < 42) return "Mediterranean Sea";

  return null;
}

function normalizeWaterName(name = "") {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(
      /\b(lac|lake|lago|laguna|río|rio|river|fleuve|rivière|fluss|riv|riviere)\b/g,
      "river"
    )
    .replace(/\s+/g, " ")
    .trim();
}

function matchFreshwaterName(displayNorm, item) {
  const nameNorm = normalizeWaterName(item.waterbody);
  if (displayNorm.includes(nameNorm)) return true;

  if (Array.isArray(item.aliases)) {
    for (const alias of item.aliases) {
      if (displayNorm.includes(normalizeWaterName(alias))) return true;
    }
  }
  return false;
}

function findFreshwaterMatch(freshwaterData, placeInfo, lat, lon) {
  if (!Array.isArray(freshwaterData)) return null;

  const rawDisplay = `${placeInfo?.display_name || ""} ${placeInfo?.name || ""}`;
  const displayNorm = normalizeWaterName(rawDisplay);

  console.log("💧 Checking freshwater match for:", rawDisplay);

  let geoMatch = freshwaterData.find((f) => {
    const b = f.bounds;
    return (
      b &&
      lat >= b.latMin &&
      lat <= b.latMax &&
      lon >= b.lonMin &&
      lon <= b.lonMax
    );
  });
  if (geoMatch) {
    console.log("✅ Geo match:", geoMatch.waterbody);
    return geoMatch;
  }

  for (const f of freshwaterData) {
    if (matchFreshwaterName(displayNorm, f)) {
      console.log("✅ Name or alias match:", f.waterbody);
      return f;
    }
  }

  const continent = placeInfo?.address?.continent?.toLowerCase?.() || "";
  const regionMatch = freshwaterData.find(
    (f) => f.region && continent.includes(f.region.toLowerCase())
  );
  if (regionMatch) {
    console.log("⚠️ Region fallback:", regionMatch.waterbody);
    return regionMatch;
  }

  console.log("🚫 No freshwater match for:", rawDisplay);
  return null;
}

export default function useDangerInfoView({ lat, lon }) {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("GLOBAL");
  const [error, setError] = useState(null);
  const [zone, setZone] = useState("land");
  const [place, setPlace] = useState(null);

  useEffect(() => {
    if (lat == null || lon == null) return;

    const detected = detectJsonRegion(lat, lon);
    setRegion(detected);

    (async () => {
      try {
        const placeInfo = await reverseGeocode(lat, lon);
        setPlace(placeInfo);
        const habitat = getHabitatZone(placeInfo) || "land";
        setZone(habitat);
        const tokens = tokensFromPlace(placeInfo);

        const [
          marineFish,
          freshwaterFish,
          wildlifeArr,
          envArr,
          diseaseArr,
          warZonesArr,
        ] = await Promise.all([
          fetch("/JSON/venomousFish_marine.json").then((r) => r.json()),
          fetch("/JSON/freshwater_dangerous.json").then((r) => r.json()),
          fetch("/JSON/wildlifeRisks.json").then((r) => r.json()),
          fetch("/JSON/environmentalHazards.json").then((r) => r.json()),
          fetch("/JSON/diseaseRisks.json").then((r) => r.json()),
          fetch("/JSON/war_zones.json").then((r) => r.json()),
        ]);

        const environmentType = detectEnvironment(lat, lon, placeInfo, freshwaterFish);
        let fishList = [];

        console.log("🐟 Environment detected:", environmentType, "at", placeInfo?.display_name);

        const marineData = marineFish;
        const freshwaterData = freshwaterFish;

        if (environmentType === "marine") {
          const marineName = getMarineRegionName(lat, lon, placeInfo);
          if (marineName) {
            const marineLower = marineName.toLowerCase();
            const match = marineData.find((f) => {
              const regionLower = (f.region || "").toLowerCase();
              if (regionLower === marineLower) return true;
              if (Array.isArray(f.aliases)) {
                return f.aliases.some(
                  (a) =>
                    marineLower === a.toLowerCase() ||
                    marineLower.includes(a.toLowerCase()) ||
                    a.toLowerCase().includes(marineLower)
                );
              }

              return false;
            });

            fishList = match?.species || [];
            if (!fishList || fishList.length === 0) {
              console.log("🐟 No marine match found for", marineName, "→ fallback to []");
              fishList = [];
            } else {
              console.log(
                "🌊 Marine match (strict):",
                marineName,
                "→",
                fishList.length,
                "species"
              );
              console.log("✅ Marine region detected:", marineName, "→", fishList.map((f) => f.name));
            }
          } else {
            console.log("🌊 No marineName detected → fallback empty list");
            fishList = [];
          }
        } else if (environmentType === "freshwater") {
          const match = findFreshwaterMatch(freshwaterData, placeInfo, lat, lon);

          if (match) {
            fishList = match.species;
            console.log(`💧 Freshwater match → ${match.waterbody} (${match.region})`, fishList.length, "species");
          } else {
            console.log("🚫 No freshwater match found for:", placeInfo?.display_name || "(unknown)");
            fishList = [];
          }
        } else {
          console.log("🏞️ Land area – no venomous/poisonous fish");
          fishList = [];
        }

        if (environmentType === "marine" || environmentType === "shoreline") {
          const freshwaterPattern =
            /\b(grass\s*carp|ctenopharyngodon\s*idella|silver\s*carp|bighead\s*carp|catla|rohu|pangasius|clarias|snakehead|tilapia|river|lake|freshwater)\b/i;

          const before = fishList.length;
          fishList = fishList.filter((f) => !freshwaterPattern.test(f.name || ""));

          if (before !== fishList.length) {
            console.log(
              "🚫 Removed freshwater-only species from marine/shoreline:",
              before - fishList.length
            );
          }
        }

        let wildRegion = findRegionDataSmart(wildlifeArr, detected, lat);
        if (!wildRegion) {
          const tokenMatch = matchByTokens(wildlifeArr, tokens);
          if (tokenMatch) wildRegion = tokenMatch;
        }

        if (!wildRegion && environmentType === "marine") {
          let sharkRegion = null;

          if (
            (lat < -25 && lat > -40 && lon > 15 && lon < 35) ||
            (lat > 32 && lat < 38 && lon > -125 && lon < -117) ||
            (lat < -30 && lat > -44 && lon > 110 && lon < 155) ||
            (lat < -30 && lat > -50 && lon > 160 && lon < 180)
          ) {
            sharkRegion = wildlifeArr.find((r) =>
              /south africa|california|australia|new zealand/i.test(r.region || "")
            );
          }

          if (sharkRegion) wildRegion = sharkRegion;
        }

        console.log(`🧭 Wildlife region match: ${wildRegion?.region || "none"} (env=${environmentType})`);

        let wildlifeList = wildRegion?.wildlife || [];

        let effectiveZone = habitat;

        if (environmentType === "marine") {
          effectiveZone = "shoreline";
        } else if (environmentType === "freshwater") {
          effectiveZone = "freshwater";
        }

        wildlifeList = wildlifeList.filter((w) => {
          if (/low|info/i.test(w.riskLevel || "")) return false;
          if (!w.habitat) return true;
          const habitats = Array.isArray(w.habitat) ? w.habitat : [w.habitat];

          if (effectiveZone === "freshwater") {
            return habitats.includes("freshwater") || habitats.includes("land");
          }

          return (
            habitats.includes(effectiveZone) ||
            (effectiveZone === "shoreline" && habitats.includes("marine"))
          );
        });

        if (!nearCoast(lat, lon)) {
          const before = wildlifeList.length;
          wildlifeList = wildlifeList.filter((w) => {
            const habitats = Array.isArray(w.habitat) ? w.habitat : [w.habitat];
            return !(
              habitats.length === 1 &&
              (habitats[0] === "marine" || habitats[0] === "shoreline")
            );
          });
          const removed = before - wildlifeList.length;
          if (removed > 0) {
            console.log(`🚫 Removed ${removed} marine-only wildlife (too far inland)`);
          }
        }

        if (lat < 60) {
          wildlifeList = wildlifeList.filter((w) => !/polar\s*bear/i.test(w.species || ""));
        }

        if (lat < 60) {
          wildlifeList = wildlifeList.filter((w) => !/polar\s*bear/i.test(w.species || ""));
        }

        wildlifeList = wildlifeList.filter((w) => {
          const n = (w.species || "").toLowerCase();

          if (n.includes("polar bear")) {
            const inTrueArctic =
              lat >= 66 &&
              lat <= 85 &&
              ((lon > -180 && lon < -20) || (lon >= 0 && lon <= 180));

            const inlandEurasia = lon > -10 && lon < 100 && lat < 72;

            return inTrueArctic && !inlandEurasia;
          }

          if (n.includes("grizzly")) {
            return lat >= 45 && lat <= 70 && lon >= -170 && lon <= -95;
          }

          if (n.includes("brown bear")) {
            return lat >= 45 && lat <= 70 && lon >= -10 && lon <= 180;
          }

          return true;
        });

        wildlifeList = wildlifeList.filter((w) => {
          const name = (w.species || "").toLowerCase();

          if (name.includes("nile crocodile")) {
            const inRange = lat >= -30 && lat <= 30 && lon >= -20 && lon <= 50;
            const inSahara = lat >= 15 && lat <= 28 && lon >= -15 && lon <= 30;
            return inRange && !inSahara;
          }

          if (name.includes("black mamba")) {
            const inRange = lat >= -35 && lat <= 10 && lon >= 15 && lon <= 50;
            const westAfrica = lon < 10 || lat > 10;
            return inRange && !westAfrica;
          }

          if (name.includes("puff adder")) {
            const inRange = lat >= -35 && lat <= 17 && lon >= -20 && lon <= 50;
            const inCongo = lat >= -5 && lat <= 5 && lon >= 10 && lon <= 30;
            return inRange && !inCongo;
          }

          if (name.includes("green mamba")) {
            const inRange = lat >= -30 && lat <= 5 && lon >= 32 && lon <= 45;
            return inRange;
          }

          if (/saltwater\s*crocodile/i.test(name)) {
            const inTropicalNorthAU = lat >= -20 && lat <= -10 && lon >= 120 && lon <= 145;

            const coastal = nearCoast(lat, lon);

            const inPNG_ID_Coast =
              lat >= -12 && lat <= 5 && lon >= 128 && lon <= 145 && coastal;

            return (inTropicalNorthAU && coastal) || inPNG_ID_Coast;
          }

          return true;
        });

        let envRegion = matchByTokens(envArr, tokens) || findRegionData(envArr, detected);
        const envList = envRegion?.hazards || [];

        const diseaseRegion =
          matchByTokens(diseaseArr, tokens) || findRegionData(diseaseArr, detected);
        const diseaseList = diseaseRegion?.diseases || [];

        let warRegion = matchByTokens(warZonesArr, tokens) || findRegionData(warZonesArr, detected);
        let warList = [];

        if (environmentType === "marine") {
          if (lat > 10 && lat < 25 && lon > 35 && lon < 48) {
            const yemenMarine = warZonesArr.find((r) => /yemen/i.test(r.region || ""));
            if (yemenMarine && Array.isArray(yemenMarine.zones)) {
              for (const z of yemenMarine.zones) {
                if (z.environment === "marine") {
                  warList.push(`${z.area} — ${z.type} (${z.riskLevel}) – ${z.notes}`);
                }
              }
            }
          }

          if (lat > -5 && lat < 15 && lon > 40 && lon < 55) {
            const somaliMarine = warZonesArr.find((r) =>
              /somali|horn\s*of\s*africa/i.test(r.region || "")
            );
            if (somaliMarine && Array.isArray(somaliMarine.zones)) {
              for (const z of somaliMarine.zones) {
                if (z.environment === "marine") {
                  warList.push(`${z.area} — ${z.type} (${z.riskLevel}) – ${z.notes}`);
                }
              }
            }
          }
        }

        if (warRegion && Array.isArray(warRegion.zones)) {
          for (const z of warRegion.zones) {
            if (z.environment && z.environment !== environmentType) {
              if (
                !(
                  environmentType === "shoreline" &&
                  ["land", "marine"].includes(z.environment)
                )
              ) {
                continue;
              }
            }

            warList.push(
              `${z.area} — ${z.type || ""} (${z.riskLevel || "Unknown"})${
                z.notes ? " – " + z.notes : ""
              }`
            );
          }
        }

        console.log(
          `⚔️ War region match: ${warRegion?.region || "none"} → ${warList.length} zones (env=${environmentType})`
        );

        console.log(
          "⚔️ War region match:",
          warList.length ? warList.join("; ") : "none",
          `(env=${environmentType})`
        );

        const looksMarine =
          environmentType === "marine" ||
          Math.abs(lat) > 55 ||
          Math.abs(lon) > 40 ||
          !freshwaterOverlap(lat, lon, freshwaterFish);

        const before = fishList.length;
        fishList = fishList.filter((f) => {
          const n = (f.name || "").toLowerCase();
          return !(
            n.includes("grass carp") ||
            n.includes("ctenopharyngodon idella") ||
            n.includes("silver carp") ||
            n.includes("bighead carp") ||
            n.includes("pangasius") ||
            n.includes("tilapia") ||
            n.includes("catla") ||
            n.includes("rohu") ||
            n.includes("snakehead") ||
            n.includes("freshwater")
          );
        });
        if (before !== fishList.length) {
          console.log(
            `🚫 FINAL FILTER removed ${before - fishList.length} freshwater species (global safeguard)`
          );
        }

        if (looksMarine) {
          const beforeMarine = fishList.length;
          fishList = fishList.filter((f) => {
            const name = (f.name || "").toLowerCase();
            return !(
              name.includes("grass carp") ||
              name.includes("ctenopharyngodon idella") ||
              name.includes("silver carp") ||
              name.includes("bighead carp") ||
              name.includes("pangasius") ||
              name.includes("tilapia") ||
              name.includes("catla") ||
              name.includes("rohu") ||
              name.includes("snakehead") ||
              name.includes("freshwater")
            );
          });
          if (beforeMarine !== fishList.length) {
            console.log(
              `🚫 FINAL FILTER removed ${
                beforeMarine - fishList.length
              } freshwater species from marine/offshore area`
            );
          }
        }

        setData({
          fish: fishList,
          wildlife: wildlifeList,
          env: envList,
          war: warList,
          disease: diseaseList,
        });
      } catch (e) {
        console.error(e);
        setError(true);
      }
    })();
  }, [lat, lon]);

  return {
    data,
    error,
    place,
    region,
    zone,
  };
}
